'use client';
import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Resource } from '@context/explorer/types';
import { useFetchUser } from '@/auth/hooks';
import { explorerAnalytics } from '@/lib/analytics';

/* 
  NOTE:
  - the ID used here should match the ID used in Card Section
  - also helps with scrolling to the particular card section on click (by making use of `href` attribute, with the same ID)
*/

interface ResourceSelectorProps {
  resources: Resource[];
  activeResource: string;
  isExpanded: boolean;
}

const ResourceSelector: React.FC<ResourceSelectorProps> = ({
  resources,
  activeResource,
  isExpanded,
}) => {
  const { isLoggedIn } = useFetchUser();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [activeResourceHeight, setActiveResourceHeight] = useState(0);
  useEffect(() => {
    const scrollToItem = (id: string) => {
      const sidebar = sectionRef.current; // Sidebar container
      const item = document.getElementById(id); // Target list item

      if (sidebar && item) {
        // Calculate position relative to the sidebar
        const sidebarTop = sidebar.getBoundingClientRect().top;
        const itemTop = item.getBoundingClientRect().top;
        const offset = itemTop - sidebarTop + sidebar.scrollTop;

        // Smoothly scroll the sidebar without affecting the main content
        sidebar.scrollTo({
          top: offset,
          behavior: 'smooth',
        });
      }
    };
    scrollToItem(`sec-${activeResource}`);
    // setTimeout(
    //   function () {
    //     scrollToItem(`sec-${activeResource}`);
    //   }.bind(this),
    //   3000
    // );
  }, [activeResource]);
  const scrollToSection = (id: string) => {
    const sidebar = sectionRef.current; // Sidebar container
    const item = document.getElementById(id); // Target list item

    if (sidebar && item) {
      // Calculate position relative to the sidebar
      const sidebarTop = sidebar.getBoundingClientRect().top;
      const itemTop = item.getBoundingClientRect().top;
      const offset = itemTop - sidebarTop + sidebar.scrollTop;

      // Smoothly scroll the sidebar without affecting the main content
      sidebar.scrollTo({
        top: offset,
        behavior: 'smooth',
      });
    }
  };
  return (
    <div
      style={{ paddingBottom: `calc(100vh - ${activeResourceHeight + 112}px)` }}
      className={`[&>*]:pl-16  group sticky top-28  h-screen overflow-y-auto overflow-hidden scrollbar-hide max-h-[calc(100vh-112px)] cursor-pointer`}
      ref={sectionRef}
      onMouseLeave={() => {
        scrollToSection(`sec-${activeResource}`);
      }}
    >
      <p className=" relative border-b border-solid border-black/10 text-[#012A38] opacity-50 py-4 mb-0 font-sora">
        Browse all APIs
      </p>{' '}
      {resources.map((resource, index) => (
        <NavSection
          activeId={activeResource}
          key={index}
          id={resource.id}
          href={`#${resource.id}`}
          isExpanded={isExpanded}
          onHeightChange={(height) => {
            setActiveResourceHeight(height);
          }}
          onClick={() => {
            explorerAnalytics.resourceSelected({
              resource: resource.id || resource.name || '',
              isLoggedIn: !!isLoggedIn,
            });
          }}
        >
          {/* <a href={`#${resource.id}`} id={`sec-${resource.id}`}> */}
          <NavHeading>{resource.name}</NavHeading>

          <NavDescription>{resource.description}</NavDescription>
          {/* </a> */}
        </NavSection>
      ))}
    </div>
  );
};

export default ResourceSelector;

export function NavHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className={`text-[0.75rem] mb-2 font-sora font-semibold`}>
      {children}
    </h3>
  );
}

export function NavDescription({ children }: { children: React.ReactNode }) {
  return <p className={`m-0   text-[1rem] font-inter font-base`}>{children}</p>;
}

/**
 * Note:
 * - When the activeId matches the id prop, a gradient is applied to the section (active highlight)
 * - Important to set the `layoutId` prop to a unique string (eg. `highlight`), reason - https://motion.dev/docs/react-layout-animations
 */
export function NavSection({
  children,
  activeId,
  id,
  href,
  isExpanded,
  onHeightChange,
  onClick,
}: {
  children: React.ReactNode;
  activeId: string | null;
  id: string;
  href: string;
  isExpanded: boolean;
  onHeightChange: (height: number) => void;
  onClick?: () => void;
}) {
  const itemRef = useRef<HTMLAnchorElement | null>(null);
  const [itemHeight, setItemHeight] = useState(0);
  const [itemWidth, setItemWidth] = useState(0);
  useEffect(() => {
    if (itemRef.current) {
      const parentElement = itemRef.current as HTMLAnchorElement;
      if (parentElement) {
        setItemHeight(parentElement.clientHeight); // Get parent height
        setItemWidth(parentElement.clientWidth); // Get parent width
        onHeightChange(parentElement.clientHeight);
      }
    }
  }, []);
  return (
    <a
      ref={itemRef}
      className={`border-b text-[#012A38] border-solid border-black/10 py-8 isolate relative block hover:bg-gradient-to-r from-[#e7eced] to-transparent `}
      href={href}
      id={`sec-${id}`}
      onClick={onClick}
    >
      {activeId === id && (
        <motion.div
          style={{ height: `${itemHeight}px`, width: `${itemWidth}px` }}
          className={`${activeId === id ? 'group-hover:hidden ' : 'bg-red-200'} ${isExpanded ? 'fixed top-28 ' : 'absolute'} inset-0   -z-10 bg-gradient-to-r from-white to-transparent after:content-[''] after:absolute after:top-8 after:left-12 after:bottom-8 after:w-0.5 after:bg-black`}
        />
      )}
      {children}
    </a>
  );
}
